import { Component, EventEmitter, Inject, Output, ViewChild } from '@angular/core';

import { Observable, of } from 'rxjs';
import { catchError } from 'rxjs/operators';

import { BootstrapPopupComponent } from '../../../../shared/bootstrap-popup/components/bootstrap-popup.component';

import { ParcTLR } from '../../parc/entities/parc-tlr.entity';
import { AntenneTLR } from '../../antenne/entities/antenne-tlr.entity';
import { GroupementParc } from '../entities/groupement-parc.entity';

import { ParcService } from '../../parc/services/parc.service';
import { AntenneService } from '../../antenne/services/antenne.service';
import { GroupementParcService } from '../services/groupement-parc.service';

enum Mode {
	CREATION,
	UPDATE
}

@Component({
	selector: 'edit-groupement-dialog',
	templateUrl: 'edit-groupement-dialog.html',
	styleUrls: ['edit-groupement-dialog.css']
})
export class EditGroupementDialogComponent {
	@ViewChild(BootstrapPopupComponent) mainFrame!: BootstrapPopupComponent;

	modes = Mode;

	// Gestion globale de la fenetre
	title!: string;
	mode!: Mode;
	groupementParc!: GroupementParc;
	
	antennes: AntenneTLR[] = [];
	selectedAntenne!: AntenneTLR;
	parcsDispos: ParcTLR[] = [];
	parcsChoisis: ParcTLR[] = [];
	allChosenParcsHaveSameResponsable: boolean = true;
	allChosenParcsHaveSameEnergie: boolean = true;
	
	getAntenneLabel = (antenne: AntenneTLR) => antenne.nom;
	getParcLabel = (parc: ParcTLR) => parc.code + ' (' + (parc.description ? parc.description : '') + ')';

	constructor( //
		@Inject(GroupementParcService) private gpService: GroupementParcService,
		@Inject(ParcService) private parcService: ParcService,
		@Inject(AntenneService) private antenneService: AntenneService
	) {
		this.antenneService.getAntenneTLRList().subscribe(
			(list: AntenneTLR[]) => this.antennes = list
		);
	}

	// Evenements à emettre
	@Output() gpCreate = new EventEmitter<GroupementParc>();
	@Output() gpUpdate = new EventEmitter<GroupementParc>();
	
	/////////////////////////
	// Actions utilisateur //
	/////////////////////////

	openForCreation() {
		// Definition du titre de la pop-up
		this.title = 'Création d\'un Groupement de parc';
		this.mode = Mode.CREATION;

		// Memorisation du GroupementParc dans son etat d'avant la saisie utilisateur
		this.groupementParc = new GroupementParc();
		
		this.parcsDispos = [];
		this.parcsChoisis = [];
		this.allChosenParcsHaveSameResponsable = true;
		this.allChosenParcsHaveSameEnergie = true;
		this.selectedAntenne = new AntenneTLR();

		// Affichage de la pop-up
		this.mainFrame.open();
	}

	openForUpdate(gp: GroupementParc) {
		// Definition du titre de la pop-up
		this.title = 'Modification du Groupement de parc ' + gp.libelle;

		// Definition du mode d'affichage
		this.mode = Mode.UPDATE;

		// Memorisation du GroupementParc dans son etat d'avant la saisie utilisateur
		this.groupementParc = gp;

		this.selectedAntenne = new AntenneTLR();
		for (let i:number = 0; i < this.antennes.length; i++) {
			if (this.antennes[i].id === gp.antenneId ) {
				this.selectedAntenne = this.antennes[i];
				break;
			}
		}
		
		this.parcsDispos = [];
		this.parcsChoisis = [];
		this.allChosenParcsHaveSameResponsable = true;
		this.allChosenParcsHaveSameEnergie = true;
		
		this.parcService.getParcTLRByAntenne(gp.antenneId).subscribe(
			(parcs: ParcTLR[]) => {
				parcs.forEach( parc => {
					if ( gp.parcIds.indexOf(parc.id) > -1 ) {
						this.parcsChoisis.push(parc);
					} else {
						this.parcsDispos.push(parc);
					}
				} );
			}
		);

		// Affichage de la pop-up
		this.mainFrame.open();
	}
	
	onAntenneChange(antenne: AntenneTLR) {
		this.parcService.getParcTLRByAntenne(antenne.id).subscribe(
			(parcs: ParcTLR[]) => {
				this.parcsDispos = parcs;
				this.parcsChoisis = [];
				this.allChosenParcsHaveSameResponsable = true;
				this.allChosenParcsHaveSameEnergie = true;
			} 
		);
	}

	onCreate() {
		// Envoi de la demande de creation de l'entite
		if (this.allChosenParcsHaveSameResponsable && this.allChosenParcsHaveSameEnergie) {
			this.buildGroupementParc();
			this.gpService.create(this.groupementParc)
				.pipe(
					catchError(
						(err: any, _caught: Observable<GroupementParc>) => {
							if (err && err.error && err.error.exceptionClass) {
								alert(err.error.message + 'Vérifiez si un parc choisi n\'est pas déjà inclus dans un autre groupement');
							} else {
								alert('Problème rencontré lors de l\'enregistrement de cette donnée');
							}
							return of(null);
						}
					)
				).subscribe(
					(gp: GroupementParc | null) => {
						if (gp) {
							this.groupementParc = gp;
							alert("Le groupement de parc " + gp.libelle + " a bien été créé");
							// Puis on ferme la fenetre de creation
							this.mainFrame.close();
							// Et on signale au parent la creation du gp
							this.gpCreate.emit(gp);
						}
					}
				)
			;
						
		}
	}

	onModify() {
		// Envoi de la demande de sauvegarde de la nouvelle version de l'entite
		if (this.allChosenParcsHaveSameResponsable && this.allChosenParcsHaveSameEnergie) {
			this.buildGroupementParc();
			this.gpService.update(this.groupementParc)
				.pipe(
					catchError(
						(err: any, _caught: Observable<GroupementParc>) => {
							if (err && err.error && err.error.exceptionClass) {
								alert(err.error.message + 'Vérifiez si un parc choisi n\'est pas déjà inclus dans un autre groupement');
							} else {
								alert('Problème rencontré lors de l\'enregistrement de cette donnée');
							}
							return of(null);
						}
					)
				).subscribe(
					(gp: GroupementParc | null) => {
						if (gp) {
							this.groupementParc = gp;
	
							// Puis on ferme la pop-up
							this.mainFrame.close();
	
							// Et on signale au parent la mise a jour du gp
							this.gpUpdate.emit(gp);
						}
					}
				)
			;
		}
	}

	private buildGroupementParc(): void {
		if (this.selectedAntenne != null && this.selectedAntenne.id != null) {
			this.groupementParc.antenneId = this.selectedAntenne.id;
		}
		this.groupementParc.parcIds = this.parcsChoisis.map ( $ => $.id );
	}
	
	getErrorHelp(): string {
		let errorMsg: string = '';
		if (!this.allChosenParcsHaveSameResponsable) {
			return 'Les parcs choisis n\'ont pas le même responsable';
		}
		if (this.allChosenParcsHaveSameEnergie) {
			return 'Les parcs choisis n\'ont pas le même énergie';
		}
		return errorMsg;
	}
	
	changesOccuredInParcs(): void {
		// verifier si même responsable pour tous les parcs choisis
		this.allChosenParcsHaveSameResponsable = this.parcsChoisis.map( p => p.responsableId ).every( (val, _i, arr) => val === arr[0] );
		this.allChosenParcsHaveSameEnergie = this.parcsChoisis.map( p => p.energie ).every( (val, _i, arr) => val === arr[0] );
	}

	onCancelRequested() {
		this.mainFrame.close();
	}
}